<script>
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { DirectUpload } from 'activestorage';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import Modal from '../../../../components/Modal.vue';
import VariablesHelp from 'dashboard/components/widgets/conversation/VariablesHelp.vue';

export default {
  components: { Modal, WootMessageEditor, VariablesHelp },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edvisibility: { type: String, default: 'public_access' },
    edgroupName: { type: String, default: 'General' },
    edsuggestTags: { type: Array, default: () => [] },
    edmediaAttachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: { showLoading: false },
      shortCode: this.edshortCode,
      content: this.edcontent,
      visibility: this.edvisibility,
      groupName: this.edgroupName || 'General',
      newGroupName: '',
      showNewGroupInput: false,
      showVarsPanel: true,
      suggestTags: [...(this.edsuggestTags || [])],
      suggestTagInput: '',
      mediaAttachments: (this.edmediaAttachments || []).map(item => ({
        ...item,
        is_local_preview: false,
      })),
      isUploadingMedia: false,
      show: true,
    };
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    existingGroups() {
      return this.$store.getters.getCannedResponseGroups || ['General'];
    },
    visibilityHelpText() {
      return this.visibility === 'public_access'
        ? this.$t('CANNED_MGMT.EDIT.FORM.VISIBILITY.PUBLIC_HELP')
        : this.$t('CANNED_MGMT.EDIT.FORM.VISIBILITY.PRIVATE_HELP');
    },
    groupInitial() {
      return (this.groupName || 'G').charAt(0).toUpperCase();
    },
    labelOptions() {
      const labels = this.$store.getters['labels/getLabels'] || [];
      return labels.map(label => label.title).sort((a, b) => a.localeCompare(b));
    },
    filteredSuggestTagOptions() {
      const input = (this.suggestTagInput || '').trim().toLowerCase();
      return this.labelOptions
        .filter(title => !this.suggestTags.includes(title))
        .filter(title => !input || title.toLowerCase().includes(input))
        .slice(0, 8);
    },
    isFormValid() {
      return !this.v$.content.$invalid && !this.v$.shortCode.$invalid;
    },
  },
  validations() {
    return {
      shortCode: { required, minLength: minLength(2) },
      content: {
        required: requiredIf(function contentRequired() {
          return this.mediaAttachments.length === 0;
        }),
      },
    };
  },
  mounted() {
    this.$store.dispatch('labels/get').catch(() => {});
  },
  unmounted() {
    this.cleanupLocalMediaPreviews();
  },
  methods: {
    normalizeTag(tag = '') {
      return String(tag).trim();
    },
    addSuggestTag(tag = this.suggestTagInput) {
      const nextTag = this.normalizeTag(tag);
      if (!nextTag) return;
      if (this.suggestTags.includes(nextTag)) {
        this.suggestTagInput = '';
        return;
      }
      this.suggestTags = [...this.suggestTags, nextTag];
      this.suggestTagInput = '';
    },
    removeSuggestTag(tag) {
      this.suggestTags = this.suggestTags.filter(item => item !== tag);
    },
    isSupportedMediaType(contentType = '') {
      return (
        contentType.startsWith('image/') || contentType.startsWith('video/')
      );
    },
    isImageMedia(media = {}) {
      return String(media.content_type || '').startsWith('image/');
    },
    isVideoMedia(media = {}) {
      return String(media.content_type || '').startsWith('video/');
    },
    async uploadCannedMedia(file) {
      return new Promise((resolve, reject) => {
        const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads');
        upload.create((error, blob) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(blob);
        });
      });
    },
    extractSignedId(blob = {}) {
      return (
        blob.signed_id ||
        blob.signedId ||
        blob.attributes?.signed_id ||
        blob.attributes?.signedId ||
        blob.blob?.signed_id ||
        blob.blob?.signedId ||
        ''
      );
    },
    async onMediaFilesSelected(event) {
      const files = Array.from(event?.target?.files || []);
      if (!files.length) return;

      this.isUploadingMedia = true;
      for (const file of files) {
        if (!this.isSupportedMediaType(file.type || '')) {
          useAlert('Only image and video files are supported.');
          continue;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          const blob = await this.uploadCannedMedia(file);
          const signedId = this.extractSignedId(blob);
          if (!signedId) {
            useAlert('Upload failed: unable to get media token.');
            continue;
          }
          const previewUrl = URL.createObjectURL(file);
          this.mediaAttachments = [
            ...this.mediaAttachments,
            {
              id: blob.id || `${signedId}-${Date.now()}`,
              signed_id: signedId,
              filename: blob.filename || file.name,
              content_type: blob.content_type || file.type,
              byte_size: blob.byte_size || file.size,
              url: previewUrl,
              is_local_preview: true,
            },
          ];
        } catch (error) {
          useAlert(error?.message || 'Failed to upload media.');
        }
      }
      this.isUploadingMedia = false;

      if (event?.target) {
        event.target.value = '';
      }
    },
    removeMediaAttachment(index) {
      const item = this.mediaAttachments[index];
      if (item?.is_local_preview && item.url?.startsWith('blob:')) {
        URL.revokeObjectURL(item.url);
      }
      this.mediaAttachments = this.mediaAttachments.filter((_, i) => i !== index);
    },
    cleanupLocalMediaPreviews() {
      this.mediaAttachments.forEach(item => {
        if (item?.is_local_preview && item.url?.startsWith('blob:')) {
          URL.revokeObjectURL(item.url);
        }
      });
    },
    resetForm() {
      this.cleanupLocalMediaPreviews();
      this.shortCode = '';
      this.content = '';
      this.suggestTags = [];
      this.suggestTagInput = '';
      this.mediaAttachments = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onGroupChange(e) {
      const val = e.target.value;
      if (val === '__new__') {
        this.showNewGroupInput = true;
        this.groupName = '';
      } else {
        this.showNewGroupInput = false;
        this.groupName = val;
      }
    },
    confirmNewGroup() {
      if (this.newGroupName.trim()) {
        this.groupName = this.newGroupName.trim();
        this.showNewGroupInput = false;
      }
    },
    editCannedResponse() {
      this.editCanned.showLoading = true;
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          visibility: this.visibility,
          group_name: this.groupName || 'General',
          suggest_tags: this.suggestTags,
          media_attachment_signed_ids: this.mediaAttachments
            .map(media => media.signed_id)
            .filter(Boolean),
        })
        .then(() => {
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => this.onClose(), 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          useAlert(
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE')
          );
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose" size="xxl">
    <div class="cr-dialog">
      <div class="cr-dialog__header">
        <h2 class="cr-dialog__title">{{ pageTitle }}</h2>
      </div>
      <div class="cr-dialog__body">
        <form class="cr-form" @submit.prevent="editCannedResponse()">
          <!-- Visibility -->
          <div class="cr-section">
            <div class="cr-toggle-group">
              <button
                type="button"
                class="cr-toggle-btn"
                :class="{ 'cr-toggle-btn--on': visibility === 'public_access' }"
                @click="visibility = 'public_access'"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M5 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.85"
                  />
                </svg>
                Shared
              </button>
              <button
                type="button"
                class="cr-toggle-btn"
                :class="{
                  'cr-toggle-btn--on': visibility === 'private_access',
                }"
                @click="visibility = 'private_access'"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"
                  />
                </svg>
                Private
              </button>
            </div>
            <p class="cr-hint">{{ visibilityHelpText }}</p>
          </div>
          <!-- Group -->
          <div class="cr-section">
            <label class="cr-label">Group selection</label>
            <div v-if="!showNewGroupInput" class="cr-select-box">
              <div class="cr-select-box__face">
                <span class="cr-avatar-sm" :style="{ background: '#6e91de' }">{{
                  groupInitial
                }}</span>
                <span class="cr-select-box__text">{{
                  groupName || 'General'
                }}</span>
              </div>
              <svg
                class="cr-select-box__arrow"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
              <select
                class="cr-select-box__native"
                :value="groupName"
                @change="onGroupChange"
              >
                <option v-for="g in existingGroups" :key="g" :value="g">
                  {{ g }}
                </option>
                <option value="__new__">
                  + {{ $t('CANNED_MGMT.GROUP.ADD_NEW') }}
                </option>
              </select>
            </div>
            <div v-else class="cr-inline-new">
              <input
                v-model="newGroupName"
                class="cr-input"
                :placeholder="$t('CANNED_MGMT.GROUP.NEW_PLACEHOLDER')"
                @keyup.enter="confirmNewGroup"
              />
              <button
                type="button"
                class="cr-btn cr-btn--sm cr-btn--fill"
                @click="confirmNewGroup"
              >
                OK
              </button>
              <button
                type="button"
                class="cr-btn cr-btn--sm"
                @click="
                  showNewGroupInput = false;
                  groupName = edgroupName || 'General';
                "
              >
                Cancel
              </button>
            </div>
          </div>

          <div class="cr-section">
            <label class="cr-label">Suggest tag</label>
            <div class="cr-tag-input">
              <input
                v-model="suggestTagInput"
                class="cr-tag-input__field"
                placeholder="Type tag and press Enter"
                @keydown.enter.prevent="addSuggestTag()"
                @keydown.tab.prevent="addSuggestTag()"
              />
              <button
                type="button"
                class="cr-btn cr-btn--sm cr-btn--fill"
                @click="addSuggestTag()"
              >
                Add
              </button>
            </div>

            <div v-if="filteredSuggestTagOptions.length > 0" class="cr-tag-options">
              <button
                v-for="option in filteredSuggestTagOptions"
                :key="option"
                type="button"
                class="cr-tag-option"
                @click="addSuggestTag(option)"
              >
                {{ option }}
              </button>
            </div>

            <div v-if="suggestTags.length > 0" class="cr-tag-bound">
              <span v-for="tag in suggestTags" :key="tag" class="cr-tag-chip">
                {{ tag }}
                <button type="button" @click="removeSuggestTag(tag)">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m6 6 12 12m0-12L6 18"
                    />
                  </svg>
                </button>
              </span>
            </div>
          </div>
          <!-- Response text -->
          <div class="cr-section">
            <label class="cr-label">Response text</label>
            <div
              class="cr-editor"
              :class="{ 'cr-editor--err': v$.content.$error }"
            >
              <WootMessageEditor
                v-model="content"
                class="message-editor [&>div]:px-1"
                :class="{ editor_warning: v$.content.$error }"
                enable-variables
                :enable-canned-responses="false"
                :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
                @blur="v$.content.$touch"
              />
            </div>
            <p v-if="v$.content.$error" class="cr-err-msg">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 8v4m0 4h.01M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0" />
              </svg>
              Enter response text or attach media.
            </p>
          </div>
          <!-- Shortcuts -->
          <div class="cr-section">
            <label class="cr-label">Shortcuts</label>
            <div
              class="cr-shortcut-input"
              :class="{ 'cr-shortcut-input--err': v$.shortCode.$error }"
            >
              <span class="cr-hash">#</span>
              <input
                v-model="shortCode"
                class="cr-shortcut-input__field"
                placeholder="Type shortcut..."
                @input="v$.shortCode.$touch"
              />
            </div>
          </div>

          <div class="cr-section">
            <label class="cr-label">Photo / video</label>
            <label class="cr-upload-btn">
              <input
                class="cr-upload-btn__native"
                type="file"
                accept="image/*,video/*"
                multiple
                @change="onMediaFilesSelected"
              />
              <span>{{ isUploadingMedia ? 'Uploading...' : 'Upload photo/video' }}</span>
            </label>

            <div v-if="mediaAttachments.length > 0" class="cr-media-grid">
              <div
                v-for="(media, index) in mediaAttachments"
                :key="`${media.signed_id}-${index}`"
                class="cr-media-card"
              >
                <img
                  v-if="isImageMedia(media)"
                  :src="media.url"
                  alt="Canned image"
                  class="cr-media-card__preview"
                />
                <video
                  v-else-if="isVideoMedia(media)"
                  :src="media.url"
                  class="cr-media-card__preview"
                  controls
                  muted
                  playsinline
                />
                <div class="cr-media-card__name">{{ media.filename }}</div>
                <button
                  type="button"
                  class="cr-media-card__remove"
                  @click="removeMediaAttachment(index)"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          <!-- Footer -->
          <div class="cr-footer">
            <button type="button" class="cr-btn cr-btn--ghost" @click="onClose">
              Cancel
            </button>
            <button
              type="submit"
              class="cr-btn cr-btn--fill"
              :disabled="!isFormValid || editCanned.showLoading || isUploadingMedia"
            >
              <span v-if="editCanned.showLoading" class="cr-spin" />
              Save
            </button>
          </div>
        </form>
        <!-- Variables -->
        <div class="cr-vars">
          <div class="cr-vars__head" @click="showVarsPanel = !showVarsPanel">
            <span class="cr-vars__icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M4.438 3c-2.5 5-2.5 10 0 16m14-16c1.38 2.76 2 5.52 1.855 8.448M8.438 8h1c1 0 1 1 2.015 3.527.786 1.972.945 3.008 1.484 3.346m-5.5.127c1.5 0 3-2 4-3.5s2.5-3.5 4-3.5m3 7v6m-3-3h6"
                />
              </svg>
            </span>
            <span class="cr-vars__title">Available Variables</span>
            <svg
              class="cr-vars__chevron"
              :class="{ 'cr-vars__chevron--open': showVarsPanel }"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
          <div v-show="showVarsPanel" class="cr-vars__body">
            <VariablesHelp />
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
.cr-dialog {
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Helvetica Neue',
    Arial,
    sans-serif;
  color: #131317;
  -webkit-font-smoothing: antialiased;
}
.cr-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #eeeeef;
}
.cr-dialog__title {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
  color: #131317;
}
.cr-dialog__body {
  display: flex;
  min-height: 0;
}
.cr-form {
  flex: 1;
  min-width: 0;
  padding: 20px 24px 16px;
  display: flex;
  flex-direction: column;
}
.cr-section {
  margin-bottom: 22px;
}
.cr-section:last-of-type {
  margin-bottom: 0;
}
.cr-label {
  display: block;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #131317;
  margin-bottom: 6px;
}
.cr-hint {
  font-size: 13px;
  line-height: 18px;
  color: #8d8d95;
  margin: 6px 0 0;
}
.cr-toggle-group {
  display: inline-flex;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  padding: 3px;
  background: #fff;
}
.cr-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 18px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #62626d;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.cr-toggle-btn + .cr-toggle-btn {
  margin-left: 4px;
}
.cr-toggle-btn--on {
  background: #eeeeef;
  color: #131317;
}
.cr-toggle-btn:hover:not(.cr-toggle-btn--on) {
  background: #f5f5f5;
}
.cr-select-box {
  position: relative;
  display: flex;
  align-items: center;
  height: 38px;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  background: #fff;
  padding: 0 12px;
  cursor: pointer;
  transition: border-color 0.15s;
}
.cr-select-box:hover {
  border-color: #c8c8ca;
}
.cr-select-box__face {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.cr-avatar-sm {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  user-select: none;
}
.cr-select-box__text {
  font-size: 14px;
  color: #131317;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.cr-select-box__arrow {
  flex-shrink: 0;
  color: #8d8d95;
}
.cr-select-box__native {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  font-family: inherit;
}
.cr-inline-new {
  display: flex;
  gap: 8px;
  align-items: center;
}
.cr-input {
  flex: 1;
  height: 38px;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #131317;
  background: #fff;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
}
.cr-input:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.08);
}

.cr-tag-input {
  display: flex;
  gap: 8px;
  align-items: center;
}

.cr-tag-input__field {
  flex: 1;
  height: 38px;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #131317;
  background: #fff;
  font-family: inherit;
  outline: none;
}

.cr-tag-input__field:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.08);
}

.cr-tag-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.cr-tag-option {
  border: 1px solid #d6e4ff;
  background: #f3f7ff;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 20px;
  cursor: pointer;
}

.cr-tag-bound {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.cr-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 2px 10px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
  line-height: 20px;
  font-weight: 700;
}

.cr-tag-chip button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: inherit;
  padding: 0;
  cursor: pointer;
}

.cr-media-grid {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.cr-media-card {
  border: 1px solid #e0e0e1;
  border-radius: 12px;
  padding: 8px;
  background: #fff;
}

.cr-media-card__preview {
  width: 100%;
  height: 92px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
}

.cr-media-card__name {
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cr-media-card__remove {
  margin-top: 6px;
  border: none;
  background: transparent;
  color: #b91c1c;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  padding: 0;
}

.cr-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #e0e0e1;
  background: #fff;
  color: #131317;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.cr-upload-btn__native {
  display: none;
}
.cr-editor {
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.15s;
}
.cr-editor:focus-within {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.08);
}
.cr-editor--err {
  border-color: #d92d20;
}
.cr-err-msg {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 6px 0 0;
  color: #d92d20;
  font-size: 13px;
  line-height: 20px;
}
.cr-shortcut-input {
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  padding: 0 12px;
  height: 38px;
  background: #fff;
  transition: border-color 0.15s;
}
.cr-shortcut-input:focus-within {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.08);
}
.cr-shortcut-input--err {
  border-color: #d92d20;
}
.cr-hash {
  color: #0066ff;
  font-weight: 600;
  font-size: 14px;
  margin-right: 4px;
  user-select: none;
}
.cr-shortcut-input__field {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #131317;
  font-family: inherit;
  line-height: 22px;
}
.cr-shortcut-input__field::placeholder {
  color: #8d8d95;
}
.cr-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 18px;
  margin-top: auto;
  border-top: 1px solid #eeeeef;
}
.cr-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 20px;
  border: 1px solid #e0e0e1;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  background: #fff;
  color: #131317;
}
.cr-btn:hover {
  background: #f5f5f5;
}
.cr-btn--sm {
  height: 32px;
  padding: 0 14px;
  font-size: 13px;
}
.cr-btn--fill {
  background: #0066ff;
  color: #fff;
  border-color: #0066ff;
}
.cr-btn--fill:hover:not(:disabled) {
  background: #0059e1;
}
.cr-btn--fill:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.cr-btn--ghost {
  background: transparent;
  border-color: #e0e0e1;
}
.cr-btn--ghost:hover {
  background: #f5f5f5;
}
.cr-spin {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: cr-spin-anim 0.6s linear infinite;
}
@keyframes cr-spin-anim {
  to {
    transform: rotate(360deg);
  }
}
.cr-vars {
  width: 260px;
  flex-shrink: 0;
  border-left: 1px solid #eeeeef;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  background: #fafafa;
}
.cr-vars__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #eeeeef;
  background: #fff;
}
.cr-vars__icon {
  display: flex;
  align-items: center;
  color: #8d8d95;
}
.cr-vars__title {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #131317;
}
.cr-vars__chevron {
  color: #8d8d95;
  transition: transform 0.25s ease;
  transform: rotate(180deg);
}
.cr-vars__chevron--open {
  transform: rotate(0deg);
}
.cr-vars__body {
  overflow-y: auto;
  padding: 12px 14px;
  flex: 1;
}
:deep(.modal-header) {
  display: none !important;
}
:deep(.modal-container) {
  background: #fff !important;
  border-radius: 12px !important;
  overflow: hidden;
}
:deep(.modal-body) {
  background: #fff !important;
  padding: 0 !important;
}
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }
  .ProseMirror-woot-style {
    @apply min-h-[7rem];
    background: #fff !important;
    border: none !important;
    border-radius: 0 !important;
    p {
      @apply text-sm;
      color: #131317 !important;
    }
  }
}
:deep(.variables-help) {
  background: transparent !important;
}
:deep(.text-n-slate-12) {
  color: #131317 !important;
}
:deep(.text-n-slate-11) {
  color: #62626d !important;
}
:deep(.text-n-slate-10) {
  color: #8d8d95 !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-solid-3),
:deep(.bg-n-background) {
  background: #fff !important;
}
:deep(.bg-n-alpha-1),
:deep(.bg-n-alpha-2),
:deep(.bg-n-alpha-3) {
  background: #f5f5f5 !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #eeeeef !important;
}
:deep(code),
:deep(.variable-chip),
:deep([class*='variable']) {
  background: #fff !important;
  border: 1px solid #eeeeef !important;
  color: #131317 !important;
}
</style>
